/** Shared visual tokens for chat onboarding + product chat. */

import { alpha } from '@mui/material/styles';

export const CHAT_PRIMARY = '#4A2CF5';
export const CHAT_PRIMARY_HOVER = '#3B23C6';
export const CHAT_PRIMARY_SOFT = '#775EFF';

/** Light purple fill for user answer bubbles (light mode default) */
export const CHAT_USER_BUBBLE_BG = '#EEE9FF';
export const CHAT_USER_BUBBLE_BORDER = '1px solid rgba(74, 44, 245, 0.14)';
export const CHAT_USER_BUBBLE_SHADOW = '0 6px 18px rgba(74, 44, 245, 0.12)';
export const CHAT_USER_TEXT = '#4A2CF5';

export const CHAT_APP_TEXT = '#171717';
export const CHAT_APP_TEXT_MUTED = '#6B6C70';
export const CHAT_HERO_TEXT = '#2B2B2E';

export const CHAT_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Mode-aware chat tokens — product Chat uses dark surfaces aligned with Search;
 * onboarding light mode keeps legacy values.
 */
export function getChatVisualTokens(theme) {
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const primaryHover = theme.palette.primary.dark;

  if (!isDark) {
    return {
      CHAT_PRIMARY: CHAT_PRIMARY,
      CHAT_PRIMARY_HOVER: CHAT_PRIMARY_HOVER,
      CHAT_USER_BUBBLE_BG,
      CHAT_USER_BUBBLE_BORDER,
      CHAT_USER_BUBBLE_SHADOW,
      CHAT_USER_TEXT,
      CHAT_APP_TEXT,
      CHAT_APP_TEXT_MUTED,
      CHAT_HERO_TEXT,
      composer: {
        surfaceBg: '#FFFFFF',
        surfaceBorder: '1px solid rgba(88, 89, 91, 0.12)',
        surfaceShadowHero: '0 10px 32px rgba(23, 15, 74, 0.1)',
        surfaceShadowDocked: '0 8px 24px rgba(23, 15, 74, 0.08)',
        focusBorderColor: 'rgba(74, 44, 245, 0.35)',
        focusShadow: '0 8px 28px rgba(74, 44, 245, 0.12)',
        inputColor: '#171717',
        placeholderColor: 'rgba(88, 89, 91, 0.5)',
        micColor: '#6B6C70',
        sendDisabledBg: 'rgba(74,44,245,0.28)',
      },
      chip: {
        bg: '#FFFFFF',
        bgHover: 'rgba(74, 44, 245, 0.04)',
        bgSelected: CHAT_USER_BUBBLE_BG,
        bgSelectedHover: '#E4DDFF',
        color: '#171717',
        colorSelected: CHAT_USER_TEXT,
        border: '1px solid rgba(88, 89, 91, 0.14)',
        borderHover: 'rgba(74, 44, 245, 0.28)',
        borderSelected: '1.5px solid rgba(74, 44, 245, 0.35)',
        borderSelectedHover: 'rgba(74, 44, 245, 0.45)',
        shadow: '0 1px 2px rgba(23, 15, 74, 0.04)',
      },
    };
  }

  return {
    CHAT_PRIMARY: primary,
    CHAT_PRIMARY_HOVER: primaryHover,
    CHAT_USER_BUBBLE_BG: alpha(primary, 0.18),
    CHAT_USER_BUBBLE_BORDER: `1px solid ${alpha(primary, 0.35)}`,
    CHAT_USER_BUBBLE_SHADOW: 'none',
    CHAT_USER_TEXT: primary,
    CHAT_APP_TEXT: theme.palette.text.primary,
    CHAT_APP_TEXT_MUTED: theme.palette.text.secondary,
    CHAT_HERO_TEXT: theme.palette.text.primary,
    composer: {
      surfaceBg: '#1E1E1E',
      surfaceBorder: '1px solid rgba(255, 255, 255, 0.14)',
      surfaceShadowHero: 'none',
      surfaceShadowDocked: 'none',
      focusBorderColor: alpha(primary, 0.5),
      focusShadow: `0 0 0 3px ${alpha(primary, 0.22)}`,
      inputColor: theme.palette.text.primary,
      placeholderColor: 'rgba(255, 255, 255, 0.55)',
      micColor: 'rgba(255, 255, 255, 0.55)',
      sendDisabledBg: alpha(primary, 0.32),
    },
    chip: {
      bg: '#1E1E1E',
      bgHover: '#2A2A2A',
      bgSelected: alpha(primary, 0.2),
      bgSelectedHover: alpha(primary, 0.26),
      color: theme.palette.text.primary,
      colorSelected: primary,
      border: '1px solid rgba(255, 255, 255, 0.14)',
      borderHover: alpha(primary, 0.45),
      borderSelected: `1.5px solid ${alpha(primary, 0.55)}`,
      borderSelectedHover: alpha(primary, 0.65),
      shadow: 'none',
    },
  };
}

export function getChatSuggestionChipSx(theme, { selected = false, isHero = false } = {}) {
  const tokens = getChatVisualTokens(theme);
  const { chip } = tokens;

  return {
    height: isHero ? 34 : 32,
    fontSize: isHero ? 13 : 12.5,
    fontWeight: 600,
    borderRadius: '999px',
    bgcolor: selected ? chip.bgSelected : chip.bg,
    color: selected ? chip.colorSelected : chip.color,
    border: selected ? chip.borderSelected : chip.border,
    boxShadow: isHero ? chip.shadow : 'none',
    transition: `background-color 180ms ${CHAT_EASE}, border-color 180ms ${CHAT_EASE}`,
    '&:hover': {
      bgcolor: selected ? chip.bgSelectedHover : chip.bgHover,
      borderColor: selected ? chip.borderSelectedHover : chip.borderHover,
    },
    '&.Mui-disabled': {
      bgcolor: chip.bg,
      color: chip.color,
      opacity: 0.55,
    },
  };
}
